import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Plus, Copy, ExternalLink, Trash2, Loader2, Mail, MessageCircle, CalendarClock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "rounded-lg border border-cy/15 bg-ink px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors focus:border-cy/60";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const formatDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function MeetingsView() {
  const [meetings, setMeetings] = useState([]);
  const [form, setForm] = useState({ title: "", client_name: "", client_email: "", scheduled_at: "" });
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(null);

  const load = () => api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (form.title.trim().length < 2) return;
    setCreating(true);
    try {
      await api.post("/meetings", { ...form, client_email: form.client_email || null });
      toast.success("Sala creada. Comparte el enlace con tu cliente.");
      setForm({ title: "", client_name: "", client_email: "", scheduled_at: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (room) => {
    navigator.clipboard.writeText(`${window.location.origin}/sala/${room}`);
    toast.success("Enlace de reunión copiado");
  };

  const waShare = (m) => {
    const link = `${window.location.origin}/sala/${m.room}`;
    const msg = `Hola${m.client_name ? ` ${m.client_name}` : ""}, te invito a nuestra reunión "${m.title}"${m.scheduled_at ? ` el ${formatDate(m.scheduled_at)}` : ""}. Entra a la videollamada aquí (no necesitas instalar nada): ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const invite = async (m) => {
    setInviting(m.id);
    try {
      await api.post(`/meetings/${m.id}/invite`);
      toast.success(`Invitación enviada a ${m.client_email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setInviting(null);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar la reunión "${m.title}"? El enlace dejará de funcionar.`)) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Reunión eliminada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-8" data-testid="meetings-view">
      <div className="rounded-xl border border-cy/10 bg-qcard p-5">
        <span className={labelCls}>Nueva sala de videollamada</span>
        <div className="flex flex-wrap gap-3">
          <input
            data-testid="meeting-form-title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Ej. Propuesta ERP — Comercial Andina"
            className={`${inputCls} min-w-[220px] flex-1`}
          />
          <input
            data-testid="meeting-form-client"
            value={form.client_name}
            onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
            placeholder="Cliente (opcional)"
            className={`${inputCls} min-w-[160px]`}
          />
          <input
            data-testid="meeting-form-email"
            type="email"
            value={form.client_email}
            onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))}
            placeholder="Correo del cliente (para invitación)"
            className={`${inputCls} min-w-[200px]`}
          />
          <input
            data-testid="meeting-form-date"
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className={`${inputCls} [color-scheme:dark]`}
            title="Fecha y hora de la reunión"
          />
          <button
            data-testid="meeting-create-btn"
            onClick={create}
            disabled={creating || form.title.trim().length < 2}
            className="flex items-center gap-2 rounded-full bg-cy px-5 py-2.5 font-display text-sm font-bold text-ink transition-all hover:bg-em disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Crear sala
          </button>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          Cada sala genera un enlace privado tipo /sala/xxxx. Compártelo por WhatsApp o envía la invitación por correo con un clic. El cliente entra sin instalar nada, directo desde el navegador.
        </p>
      </div>

      <div className="mt-6 grid gap-3" data-testid="meetings-list">
        {meetings.length === 0 && (
          <div className="rounded-xl border border-dashed border-cy/20 p-10 text-center text-sm text-slate-500" data-testid="meetings-empty">
            Aún no hay reuniones. Crea la primera con el formulario de arriba.
          </div>
        )}
        {meetings.map((m) => (
          <div key={m.id} data-testid={`meeting-item-${m.room}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-cy/10 bg-qcard p-5 transition-colors hover:border-cy/30">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-cy/20 bg-ink">
              <Video className="h-5 w-5 text-cy" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-display text-base font-bold text-slate-100">{m.title}</p>
                {m.scheduled_at && (
                  <span className="flex items-center gap-1.5 rounded-full border border-amber/40 px-2.5 py-0.5 font-mono text-[10px] text-amber" data-testid={`meeting-date-${m.room}`}>
                    <CalendarClock className="h-3 w-3" />
                    {formatDate(m.scheduled_at)}
                  </span>
                )}
              </div>
              <p className="font-mono text-xs text-slate-500">
                {m.client_name ? `${m.client_name} · ` : ""}/sala/{m.room} · {new Date(m.created_at).toLocaleDateString("es-EC")}
                {m.client_email ? ` · ${m.client_email}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`meeting-wa-${m.room}`} onClick={() => waShare(m)} title="Enviar enlace por WhatsApp" className="grid h-9 w-9 place-items-center rounded-lg border border-em/30 text-em/80 transition-colors hover:border-em hover:text-em">
                <MessageCircle className="h-4 w-4" />
              </button>
              {m.client_email && (
                <button data-testid={`meeting-invite-${m.room}`} onClick={() => invite(m)} disabled={inviting === m.id} title="Enviar invitación por correo" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/30 text-cy/80 transition-colors hover:border-cy hover:text-cy disabled:opacity-50">
                  {inviting === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                </button>
              )}
              <button data-testid={`meeting-copy-${m.room}`} onClick={() => copyLink(m.room)} title="Copiar enlace" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy">
                <Copy className="h-4 w-4" />
              </button>
              <a data-testid={`meeting-join-${m.room}`} href={`/sala/${m.room}`} target="_blank" rel="noopener noreferrer" title="Entrar a la sala" className="grid h-9 w-9 place-items-center rounded-lg border border-em/30 text-em/80 transition-colors hover:border-em hover:text-em">
                <ExternalLink className="h-4 w-4" />
              </a>
              <button data-testid={`meeting-delete-${m.id}`} onClick={() => remove(m)} title="Eliminar" className="grid h-9 w-9 place-items-center rounded-lg border border-red-500/30 text-red-400/80 transition-colors hover:border-red-400 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
